// ดันข้อมูลโต๊ะที่ Main cashier คำนวณได้ตอนนี้ขึ้น cloud ทั้งก้อน
//
// RTDB stores/{id}/tables/{table} = { items, jobs, startDate, guests } (Flutter หน้ารายละเอียดอ่าน items จากตรงนี้)
// plugin ปกติเขียน tables/tableMeta/live ทั้งก้อนทุกครั้งที่ข้อมูลเปลี่ยน แต่ข้ามรอบแรกหลัง boot
// และ write ที่ค้างตอน offline หายถ้าปิด browser — เลยเขียนทั้งก้อนซ้ำตอน boot / เน็ตกลับมา / กดปุ่ม
// ไม่ต้องเทียบอะไรกับ cloud: ฝั่งที่ฟังอยู่ได้แค่ส่วนที่ค่าเปลี่ยนจริง
//
// ไม่ replay jobs/logs — jobchain เป็นต้นทางอยู่แล้ว ที่นี่แค่ดันผลที่คำนวณแล้วขึ้นไป

import { getDatabase, onValue, ref, update } from "firebase/database";
import { RunMode } from "~~/interface/Pos";
import { buildLiveSummary, buildTableMeta, isLiveSyncSuspended, writeAllTableDataToFirestore } from "./useTableSync";

// write ตอนเน็ตไม่ดี promise ค้างได้เรื่อยๆ — ปุ่มต้องจบให้ได้ ไม่งั้น spinner หมุนไม่หยุด
const WRITE_TIMEOUT_MS = 20_000;

export type CloudSyncErrorCode = "not_main_cashier" | "not_ready" | "offline" | "timeout";

export class CloudSyncError extends Error {
  code: CloudSyncErrorCode;
  constructor(code: CloudSyncErrorCode) {
    super(code);
    this.code = code;
  }
}

export interface CloudSyncResult {
  total: number; // จำนวนโต๊ะที่เปิดอยู่และถูกเขียนขึ้นไป
}

function withTimeout<T>(promise: Promise<T>, ms = WRITE_TIMEOUT_MS): Promise<T> {
  let timer: any;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new CloudSyncError("timeout")), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function isRtdbConnected(): Promise<boolean> {
  return new Promise((resolve) => {
    onValue(ref(getDatabase(), ".info/connected"), (snap) => resolve(snap.val() === true), { onlyOnce: true });
  });
}

function isMainCashier() {
  return useRunningMode().getMode() === RunMode.MAIN_CASHIER;
}

// true หลัง mobile.items.plugin ส่งทั้งก้อนรอบแรกแล้ว (trigger แรกหลังคำนวณ tables)
// ก่อนหน้านั้น tables อาจยังว่าง — เน็ตกลับมา/กดปุ่มตอนนั้นจะเอาค่าว่างไปทับของจริงบน cloud
let initialPublished = false;

export function isCloudSyncReady() {
  return initialPublished && usePosStore().tableStateLoaded;
}

/**
 * เขียนทุกอย่างขึ้น RTDB + Firestore ในรอบเดียว
 *
 * withTableState: ปุ่มเท่านั้นที่เขียน RTDB tableState ทั้งก้อน
 * boot/เน็ตกลับมา ไม่เขียน — tableState ในเครื่องมาจาก snapshot ของ RTDB อยู่แล้ว
 * ถ้าเขียนทั้งก้อนอาจทับที่เครื่องอื่นเพิ่งแก้
 */
async function writeEverything(withTableState: boolean): Promise<CloudSyncResult> {
  const jobsStore = useJobsStore();
  const posStore = usePosStore();
  // snapshot ก่อน await ทั้งหมด — tables/tableMeta/live ต้องมาจากชุดเดียวกัน
  const tables = jobsStore.$state.tables || {};
  const tableMeta = buildTableMeta(jobsStore);
  const live = buildLiveSummary(jobsStore);
  const tableState = posStore.tableState || {};

  // update ที่ root ด้วย key ระดับเดียว = set ทั้ง node (โต๊ะที่ปิดแล้วหลุดออกไปเอง) และขึ้นพร้อมกันทั้งชุด
  const rtdb: Record<string, any> = { tables, tableMeta, live };
  if (withTableState) rtdb.tableState = tableState;

  await Promise.all([
    update(useFirebase().tablesRef().parent!, rtdb),
    writeAllTableDataToFirestore({ tableMeta, tableState, live }),
  ]);

  return { total: Object.keys(tables).length };
}

/**
 * ส่งทั้งก้อนแบบอัตโนมัติ
 * - trigger แรกของ mobile.items.plugin (แทนการข้ามรอบแรกแบบเดิม)
 * - เน็ตกลับมา (cloudSync.plugin) — หลังส่งรอบแรกไปแล้วเท่านั้น
 */
export async function syncAllToCloud(): Promise<CloudSyncResult | null> {
  if (!isMainCashier()) return null;
  // ช่วงหลังปิดร้านก่อนหน้า reload: เน็ตกลับมาก็ห้าม push ยอดของรอบที่ปิดแล้วทับ live = 0
  // (เหตุผล + ตัวอย่างดู liveSyncSuspended ใน useTableSync.ts)
  if (isLiveSyncSuspended()) return null;
  const result = await writeEverything(false);
  initialPublished = true;
  return result;
}

/**
 * ปุ่ม "Sync to Cloud": เขียนสถานะล่าสุดทับทั้งหมด ไม่ reload หน้า
 * RTDB tables, tableMeta, live, tableState + Firestore table_meta, table_state, live
 */
export async function forceSyncToCloud(): Promise<CloudSyncResult> {
  if (!isMainCashier()) throw new CloudSyncError("not_main_cashier");
  // ยังโหลด jobs/tableState ไม่ครบ เขียนไปตอนนี้ = เอาค่าว่างไปทับของจริงบน cloud
  if (!isCloudSyncReady()) throw new CloudSyncError("not_ready");
  if (!(await isRtdbConnected())) throw new CloudSyncError("offline");

  return withTimeout(writeEverything(true));
}
